import type { ApiRequestPath } from "./generated/openapi";

export type ApiError = {
  code?: string;
  message: string;
  status?: number;
};

export type ApiResponse<T> = {
  data: T | null;
  error: ApiError | null;
};

const API_BASE_URL =
  process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "http://localhost:4000";

export function apiUrl(path: ApiRequestPath): string {
  if (API_BASE_URL.endsWith("/api") && path.startsWith("/api/")) {
    return `${API_BASE_URL}${path.slice(4)}`;
  }

  return `${API_BASE_URL}${path}`;
}

export function queryString<T extends object>(
  params: T,
): "" | `?${string}` {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      search.set(key, String(value));
    }
  });
  const value = search.toString();
  return value ? `?${value}` : "";
}

export async function apiRequest<T>(
  path: ApiRequestPath,
  options: RequestInit = {},
): Promise<ApiResponse<T>> {
  try {
    const isFormData =
      typeof FormData !== "undefined" && options.body instanceof FormData;
    const headers: Record<string, string> = {
      ...(isFormData ? {} : { "Content-Type": "application/json" }),
      ...((options.headers as Record<string, string> | undefined) || {}),
    };

    const response = await fetch(apiUrl(path), {
      ...options,
      headers,
      credentials: "include",
    });
    const text = await response.text();
    const data = text ? JSON.parse(text) : null;
    if (!response.ok) {
      const responseError = data?.error as Partial<ApiError> | undefined;
      return {
        data: null,
        error: {
          code: responseError?.code,
          message: responseError?.message || response.statusText,
          status: response.status,
        },
      };
    }
    return { data, error: null };
  } catch (error: unknown) {
    return {
      data: null,
      error: {
        message: error instanceof Error ? error.message : String(error),
      },
    };
  }
}
